import DashboardWidgetSettings from "@/domain/entities/user/DashboardWidgetSettings";
import UserProfile from "@/domain/entities/user/UserProfile";
import { queryKeys } from "@/lib/queryKeys";
import { dashboardWidgetSettingsToSDKMapper } from "@/mappers/user/dashboardWidgetSettingsMapper";
import { userToSDKMapper } from "@/mappers/user/userMapper";
import { useSdk } from "@cs_teamwaydev/mailvista-sdk_react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import useGetUserProfile from "../useGetUserProfile";
import useErrorToast from "../utils/useErrorToast";

const useSetHomepageSettings = () => {
    const { userManager } = useSdk();
    const showToast = useErrorToast();
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const { data: user } = useGetUserProfile();

    const setDashboardWidgetSettings = async (
        widgetSettings: DashboardWidgetSettings,
    ): Promise<void> => {
        if (!user) {
            throw new Error("User is not available");
        }

        const previousUser = user;

        const newUser = new UserProfile({
            ...user,
            settings: {
                ...user.settings,
                displayPreferences: {
                    ...user.settings.displayPreferences,
                    dashboardWidgets: {
                        ...user.settings.displayPreferences.dashboardWidgets,
                        ...widgetSettings,
                    },
                },
            },
        });

        const newUserSDK = userToSDKMapper(newUser);

        queryClient.setQueryData(queryKeys.user.profile(), newUserSDK);

        const dashboardWidgetSettingsSDK =
            dashboardWidgetSettingsToSDKMapper(widgetSettings);

        try {
            await userManager.setDashboardWidgets(dashboardWidgetSettingsSDK);
        } catch (error) {
            console.error("Error setting widget settings", error);

            showToast({
                titleTranslationKey: t("errors.widgetsSettings"),
            });

            queryClient.setQueryData(queryKeys.user.profile(), previousUser);
        }
    };

    return { setDashboardWidgetSettings };
};

export default useSetHomepageSettings;
